import { useRef, useEffect, useCallback } from 'react'
import _ from 'lodash'

type Options = {
  canLoadMore: boolean
  onLoadMore: () => void
  root?: Element | null
}

export const useScroll = ({ canLoadMore, onLoadMore, root }: Options) => {
  const onLoadMoreRef = useRef(() => {})
  const targetRef = useRef<Element | null | undefined>()
  const unobserveRef = useRef<() => void>()

  useEffect(() => {
    onLoadMoreRef.current = _.debounce(onLoadMore, 300)
  }, [onLoadMore])

  const observeTarget = useCallback(() => {
    if (unobserveRef.current) {
      unobserveRef.current()
      unobserveRef.current = undefined
    }
    const target = targetRef.current
    if (!target || !canLoadMore) {
      return
    }
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(
          ({ isIntersecting }) => isIntersecting && onLoadMoreRef.current()
        )
      },
      {
        root: root,
      }
    )
    observer.observe(target)
    unobserveRef.current = () => observer.unobserve(target)

    return () => observer.disconnect()
  }, [canLoadMore, root])

  const setTargetRef = useCallback(
    (target: Element | null) => {
      targetRef.current = target
      observeTarget()
    },
    [observeTarget]
  )

  return [setTargetRef]
}
